import React, { useCallback, useEffect, useRef, useState } from 'react'
import { CDN_URI } from '../../../constants/cdn'
import { isProline } from '../../../helpers'

const SALE_URL = isProline()
  ? 'https://www.76proline.com/sl/l/Patriotic?sortOptions=2'
  : 'https://shop.76fireworks.com/fsl/l/spark_the_season_sale_2026'

const COUNTDOWN_TARGET_DATE = new Date(2026, 6, 4, 0, 0, 0, 0)
const FINAL_BANNER_IMAGE_PATH = '_images/Banners/Happy 250th, America!.png'
const FINAL_BANNER_IMAGE_URL = encodeURI(`${CDN_URI}${FINAL_BANNER_IMAGE_PATH}`)
const FINAL_BANNER_ALT = 'Happy 250th, America!'
const TRANSITION_DURATION_MS = 2600
const FINAL_BANNER_COOKIE_NAME = 'spirit_250_banner_seen'
const FINAL_BANNER_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365
const FINAL_BANNER_AUTO_SHOW_DATE = new Date(2026, 6, 12, 0, 0, 0, 0)
const REVEAL_PREVIEW_PARAM = 'bannerPreview'
const REVEAL_PREVIEW_VALUE = 'reveal'
const LOCAL_PREVIEW_HOSTS = ['localhost', '127.0.0.1', '::1']
const FIREWORK_COUNT = 8
const ENABLE_BANNER_PREVIEW =
  process.env.REACT_APP_ENABLE_BANNER_PREVIEW === 'true'

const getCountdownParts = (now = new Date()) => {
  const difference = Math.max(
    COUNTDOWN_TARGET_DATE.getTime() - now.getTime(),
    0,
  )

  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((difference % (1000 * 60)) / 1000),
    isComplete: difference === 0,
  }
}

const hasFinalBannerCookie = () => {
  if (typeof document === 'undefined') {
    return false
  }

  return document.cookie
    .split(';')
    .some((cookie) => cookie.trim().startsWith(`${FINAL_BANNER_COOKIE_NAME}=`))
}

const setFinalBannerCookie = () => {
  if (typeof document === 'undefined') {
    return
  }

  document.cookie = [
    `${FINAL_BANNER_COOKIE_NAME}=true`,
    `Max-Age=${FINAL_BANNER_COOKIE_MAX_AGE_SECONDS}`,
    'Path=/',
    'SameSite=Lax',
  ].join('; ')
}

const isRevealButtonPreview = () => {
  if (typeof window === 'undefined') {
    return false
  }

  const isRevealPreviewRequest =
    new URLSearchParams(window.location.search).get(REVEAL_PREVIEW_PARAM) ===
    REVEAL_PREVIEW_VALUE
  const isLocalHost = LOCAL_PREVIEW_HOSTS.includes(window.location.hostname)
  const isDevelopmentBuild = process.env.NODE_ENV !== 'production'

  return (
    isRevealPreviewRequest &&
    (isDevelopmentBuild || isLocalHost || ENABLE_BANNER_PREVIEW)
  )
}

const shouldAutoShowFinalBanner = (now = new Date()) => {
  if (isRevealButtonPreview()) {
    return false
  }

  return now.getTime() >= FINAL_BANNER_AUTO_SHOW_DATE.getTime()
}

const shouldShowFinalBanner = () => {
  if (isRevealButtonPreview()) {
    return false
  }

  return hasFinalBannerCookie() || shouldAutoShowFinalBanner()
}

const FinalBannerArtwork: React.FC<{ variant: 'final' | 'overlay' }> = ({
  variant,
}) => {
  const [finalBannerImageFailed, setFinalBannerImageFailed] = useState(false)

  if (finalBannerImageFailed) {
    return (
      <div className={`july-fourth-countdown-banner__${variant}-fallback`}>
        <span className={`july-fourth-countdown-banner__${variant}-kicker`}>
          America's 250th Birthday
        </span>
        <span className={`july-fourth-countdown-banner__${variant}-headline`}>
          {FINAL_BANNER_ALT}
        </span>
      </div>
    )
  }

  return (
    <img
      className={`july-fourth-countdown-banner__${variant}-image`}
      src={FINAL_BANNER_IMAGE_URL}
      alt={
        variant === 'overlay'
          ? `${FINAL_BANNER_ALT} celebration preview`
          : FINAL_BANNER_ALT
      }
      onError={() => setFinalBannerImageFailed(true)}
    />
  )
}

const JulyFourthFinalBanner: React.FC = () => (
  <section
    className="july-fourth-countdown-banner july-fourth-countdown-banner--final"
    aria-label="Happy 250th America banner"
  >
    <FinalBannerArtwork variant="final" />
  </section>
)

const JulyFourthCountdownBannerContent: React.FC = () => {
  const [countdown, setCountdown] = useState(getCountdownParts)
  const [showFinalBanner, setShowFinalBanner] = useState(() =>
    shouldShowFinalBanner(),
  )
  const [isTransitioning, setIsTransitioning] = useState(false)
  const transitionTimer = useRef<number | undefined>()
  const showCelebrationOverlay =
    !showFinalBanner && (countdown.isComplete || isRevealButtonPreview())

  useEffect(() => {
    const finalBannerImage = new Image()
    finalBannerImage.src = FINAL_BANNER_IMAGE_URL
  }, [])

  useEffect(() => {
    return () => {
      if (transitionTimer.current) {
        window.clearTimeout(transitionTimer.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!showCelebrationOverlay) {
      return undefined
    }

    const previousBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousBodyOverflow
    }
  }, [showCelebrationOverlay])

  const startFinalBannerTransition = useCallback(
    (persistCookie = false) => {
      if (showFinalBanner || isTransitioning) {
        return
      }

      if (persistCookie) {
        setFinalBannerCookie()
      }

      setIsTransitioning(true)
      transitionTimer.current = window.setTimeout(() => {
        setShowFinalBanner(true)
      }, TRANSITION_DURATION_MS)
    },
    [isTransitioning, showFinalBanner],
  )

  useEffect(() => {
    if (showFinalBanner || isTransitioning) {
      return
    }

    const timer = window.setInterval(() => {
      const nextCountdown = getCountdownParts()
      setCountdown(nextCountdown)

      if (shouldAutoShowFinalBanner()) {
        window.clearInterval(timer)
        setShowFinalBanner(true)
        return
      }

      if (nextCountdown.isComplete) {
        window.clearInterval(timer)
      }
    }, 1000)

    return () => {
      window.clearInterval(timer)
    }
  }, [isTransitioning, showFinalBanner, startFinalBannerTransition])

  const renderFireworks = (className: string) => (
    <div aria-hidden="true" className={className}>
      {Array.from({ length: FIREWORK_COUNT }, (_, index) => (
        <span key={index} />
      ))}
    </div>
  )

  if (showFinalBanner) {
    return <JulyFourthFinalBanner />
  }

  const timeSegments = [
    { label: 'Days', value: countdown.days },
    { label: 'Hours', value: countdown.hours },
    { label: 'Minutes', value: countdown.minutes },
    { label: 'Seconds', value: countdown.seconds },
  ]

  return (
    <>
      {showCelebrationOverlay ? (
        <div
          aria-label={FINAL_BANNER_ALT}
          aria-modal="true"
          className={`july-fourth-countdown-banner__celebration-overlay${
            isTransitioning
              ? ' july-fourth-countdown-banner__celebration-overlay--transitioning'
              : ''
          }`}
          role="dialog"
        >
          {isTransitioning
            ? renderFireworks(
                'july-fourth-countdown-banner__fireworks july-fourth-countdown-banner__overlay-fireworks',
              )
            : null}
          <span
            aria-hidden="true"
            className="july-fourth-countdown-banner__overlay-burst july-fourth-countdown-banner__overlay-burst--left"
          />
          <span
            aria-hidden="true"
            className="july-fourth-countdown-banner__overlay-burst july-fourth-countdown-banner__overlay-burst--right"
          />
          <div className="july-fourth-countdown-banner__overlay-content">
            <div className="july-fourth-countdown-banner__overlay-artwork">
              <FinalBannerArtwork variant="overlay" />
            </div>
            <p className="july-fourth-countdown-banner__overlay-copy">
              Join us in celebrating America's Semiquincentennial. We hope you
              have a safe, happy, and spectacular Fourth of July!
            </p>
            {!isTransitioning ? (
              <button
                className="july-fourth-countdown-banner__overlay-button"
                type="button"
                onClick={() =>
                  startFinalBannerTransition(!isRevealButtonPreview())
                }
              >
                Start the Celebration!
              </button>
            ) : (
              <span className="july-fourth-countdown-banner__overlay-status">
                Lighting the finale
              </span>
            )}
          </div>
        </div>
      ) : null}

      <section
        className={`july-fourth-countdown-banner${
          isTransitioning ? ' july-fourth-countdown-banner--transitioning' : ''
        }`}
        aria-label="Fourth of July countdown sale banner"
      >
        <span
          aria-hidden="true"
          className="july-fourth-countdown-banner__burst july-fourth-countdown-banner__burst--left"
        />
        <span
          aria-hidden="true"
          className="july-fourth-countdown-banner__burst july-fourth-countdown-banner__burst--right"
        />
        {isTransitioning
          ? renderFireworks('july-fourth-countdown-banner__fireworks')
          : null}

        <div className="july-fourth-countdown-banner__content">
          <div className="july-fourth-countdown-banner__message">
            <span className="july-fourth-countdown-banner__kicker">
              Countdown to the 4th of July
            </span>
            <span className="july-fourth-countdown-banner__headline">
              America's 250th Birthday
            </span>
          </div>

          <time
            className="july-fourth-countdown-banner__timer"
            dateTime={COUNTDOWN_TARGET_DATE.toISOString()}
          >
            {timeSegments.map((segment) => (
              <span
                className="july-fourth-countdown-banner__time-segment"
                key={segment.label}
              >
                <span className="july-fourth-countdown-banner__time-value">
                  {segment.value.toString().padStart(2, '0')}
                </span>
                <span className="july-fourth-countdown-banner__time-label">
                  {segment.label}
                </span>
              </span>
            ))}
          </time>

          <a className="july-fourth-countdown-banner__link" href={SALE_URL}>
            Shop Now
          </a>
        </div>
      </section>
    </>
  )
}

const JulyFourthCountdownBanner: React.FC = () => {
  if (isProline()) {
    return <JulyFourthFinalBanner />
  }

  return <JulyFourthCountdownBannerContent />
}

export default JulyFourthCountdownBanner
